import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';
import { getLocalDateString } from '../utils/dateUtils';

// Generate a simple UUID for agenda/action items
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Calculate end time from start time and duration
function calculateEndTime(startTime, durationMinutes) {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
}

export function useMeetings() {
  const { user } = useAuth();

  // State for all three collections
  const [myMeetings, setMyMeetings] = useState([]);
  const [sharedMeetings, setSharedMeetings] = useState([]);
  const [accountLinks, setAccountLinks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [series, setSeries] = useState([]);
  const [sharedSeries, setSharedSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Combined meetings (my + shared)
  const meetings = useMemo(() => {
    return [...myMeetings, ...sharedMeetings];
  }, [myMeetings, sharedMeetings]);

  // Combined series (my + shared) for virtual instance generation
  const allSeries = useMemo(() => {
    return [...series, ...sharedSeries];
  }, [series, sharedSeries]);

  // Get linked user IDs who I share meetings with (for populating viewableBy)
  const getLinkedUserIdsForSharing = useCallback((links) => {
    if (!user?.uid) return [];
    return links
      .filter(link => {
        // Check if I share meetings with this link
        const mySharing = link.userA === user.uid ? link.userASharing : link.userBSharing;
        return mySharing?.meetings && link.status === 'active';
      })
      .map(link => link.userA === user.uid ? link.userB : link.userA);
  }, [user?.uid]);

  // Refs to hold current link state (prevents stale closures in dual listeners)
  const linksAsARef = useRef([]);
  const linksAsBRef = useRef([]);
  const mergeTimeoutRef = useRef(null);

  // Merge and dedupe links from both queries (debounced to prevent double updates)
  const mergeLinks = useCallback(() => {
    // Debounce: only merge after 50ms of no changes
    if (mergeTimeoutRef.current) {
      clearTimeout(mergeTimeoutRef.current);
    }
    mergeTimeoutRef.current = setTimeout(() => {
      const allLinks = [...linksAsARef.current, ...linksAsBRef.current];
      const uniqueLinks = Array.from(
        new Map(allLinks.map((link) => [link.id, link])).values()
      );
      setAccountLinks(uniqueLinks);
    }, 50);
  }, []);

  // Listen to account links for sharing (using userA/userB queries)
  useEffect(() => {
    if (!user?.uid) {
      linksAsARef.current = [];
      linksAsBRef.current = [];
      setAccountLinks([]);
      return;
    }

    const queryA = query(
      collection(db, 'accountLinks'),
      where('userA', '==', user.uid),
      where('status', '==', 'active')
    );

    const queryB = query(
      collection(db, 'accountLinks'),
      where('userB', '==', user.uid),
      where('status', '==', 'active')
    );

    const unsubscribeA = onSnapshot(
      queryA,
      (snapshot) => {
        linksAsARef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [useMeetings] accountLinks-A listener error:', err.message)
    );

    const unsubscribeB = onSnapshot(
      queryB,
      (snapshot) => {
        linksAsBRef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [useMeetings] accountLinks-B listener error:', err.message)
    );

    return () => {
      unsubscribeA();
      unsubscribeB();
      if (mergeTimeoutRef.current) {
        clearTimeout(mergeTimeoutRef.current);
      }
    };
  }, [user?.uid, mergeLinks]);

  // Fetch shared meetings from linked accounts (query by viewableBy array)
  const fetchSharedMeetings = useCallback(async () => {
    if (!user?.uid || accountLinks.length === 0) {
      setSharedMeetings([]);
      return;
    }

    // Build a map of linked user IDs to their display info
    const linkedUserInfo = {};
    for (const link of accountLinks) {
      const isUserA = link.userA === user.uid;
      const theirSharing = isUserA ? link.userBSharing : link.userASharing;
      if (!theirSharing?.meetings) continue;

      const linkedUserId = isUserA ? link.userB : link.userA;
      linkedUserInfo[linkedUserId] = {
        displayName: isUserA ? link.userBDisplayName : link.userADisplayName,
        email: isUserA ? link.userBEmail : link.userAEmail
      };
    }

    // If no linked users share meetings with me, no need to query
    if (Object.keys(linkedUserInfo).length === 0) {
      setSharedMeetings([]);
      return;
    }

    try {
      // Query meetings where current user is in the viewableBy array
      const meetingsQuery = query(
        collection(db, 'meetings'),
        where('viewableBy', 'array-contains', user.uid),
        where('excludeFromSharing', '!=', true),
        limit(100)
      );
      const snapshot = await getDocs(meetingsQuery);

      const shared = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        .map((meeting) => {
          const ownerInfo = linkedUserInfo[meeting.userId] || {};
          return {
            ...meeting,
            isShared: true,
            owner: ownerInfo.displayName || ownerInfo.email?.split('@')[0] || 'Shared',
            ownerDisplayName: ownerInfo.displayName,
            ownerEmail: ownerInfo.email
          };
        });

      setSharedMeetings(shared);
    } catch (error) {
      console.error('Error fetching shared meetings:', error);
      setSharedMeetings([]);
    }
  }, [user?.uid, accountLinks]);

  // Refetch shared meetings when account links change
  useEffect(() => {
    fetchSharedMeetings();
  }, [fetchSharedMeetings]);

  // Fetch shared series from linked accounts (query by viewableBy array)
  const fetchSharedSeries = useCallback(async () => {
    if (!user?.uid || accountLinks.length === 0) {
      setSharedSeries([]);
      return;
    }

    // Build a map of linked user IDs to their display info
    const linkedUserInfo = {};
    for (const link of accountLinks) {
      const isUserA = link.userA === user.uid;
      const theirSharing = isUserA ? link.userBSharing : link.userASharing;
      if (!theirSharing?.meetings) continue;

      const linkedUserId = isUserA ? link.userB : link.userA;
      linkedUserInfo[linkedUserId] = {
        displayName: isUserA ? link.userBDisplayName : link.userADisplayName,
        email: isUserA ? link.userBEmail : link.userAEmail
      };
    }

    // If no linked users share meetings with me, no need to query
    if (Object.keys(linkedUserInfo).length === 0) {
      setSharedSeries([]);
      return;
    }

    try {
      // Query series where current user is in the viewableBy array
      const seriesQuery = query(
        collection(db, 'meetingSeries'),
        where('viewableBy', 'array-contains', user.uid),
        where('isArchived', '==', false),
        where('excludeFromSharing', '!=', true),
        limit(50)
      );
      const snapshot = await getDocs(seriesQuery);

      const shared = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .map((s) => {
          const ownerInfo = linkedUserInfo[s.userId] || {};
          return {
            ...s,
            isShared: true,
            owner: ownerInfo.displayName || ownerInfo.email?.split('@')[0] || 'Shared',
            ownerDisplayName: ownerInfo.displayName,
            ownerEmail: ownerInfo.email
          };
        });

      setSharedSeries(shared);
    } catch (error) {
      console.error('Error fetching shared series:', error);
      setSharedSeries([]);
    }
  }, [user?.uid, accountLinks]);

  // Refetch shared series when account links change
  useEffect(() => {
    fetchSharedSeries();
  }, [fetchSharedSeries]);

  // Real-time listeners for all collections
  useEffect(() => {
    if (!user?.uid) {
      setMyMeetings([]);
      setTemplates([]);
      setSeries([]);
      setLoading(false);
      return;
    }

    let loadingCount = 3;
    const decrementLoading = () => {
      loadingCount--;
      if (loadingCount === 0) setLoading(false);
    };

    // Meetings listener (my meetings only)
    const meetingsQuery = query(
      collection(db, 'meetings'),
      where('userId', '==', user.uid),
      orderBy('date', 'asc')
    );

    const unsubMeetings = onSnapshot(
      meetingsQuery,
      (snapshot) => {
        setMyMeetings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        decrementLoading();
      },
      (err) => {
        console.error('Error fetching meetings:', err);
        setError(err.message);
        decrementLoading();
      }
    );

    // Templates listener
    const templatesQuery = query(
      collection(db, 'meetingTemplates'),
      where('userId', '==', user.uid),
      where('isArchived', '==', false)
    );

    const unsubTemplates = onSnapshot(
      templatesQuery,
      (snapshot) => {
        setTemplates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        decrementLoading();
      },
      (err) => {
        console.error('Error fetching templates:', err);
        setError(err.message);
        decrementLoading();
      }
    );

    // Series listener
    const seriesQuery = query(
      collection(db, 'meetingSeries'),
      where('userId', '==', user.uid),
      where('isArchived', '==', false)
    );

    const unsubSeries = onSnapshot(
      seriesQuery,
      (snapshot) => {
        setSeries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        decrementLoading();
      },
      (err) => {
        console.error('Error fetching series:', err);
        setError(err.message);
        decrementLoading();
      }
    );

    return () => {
      unsubMeetings();
      unsubTemplates();
      unsubSeries();
    };
  }, [user?.uid]);

  // ============ Template Operations ============

  const addTemplate = async (data) => {
    if (!user?.uid) throw new Error('Must be logged in');

    return await addDoc(collection(db, 'meetingTemplates'), {
      ...data,
      userId: user.uid,
      isArchived: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const updateTemplate = async (id, data) => {
    await updateDoc(doc(db, 'meetingTemplates', id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  };

  const archiveTemplate = async (id) => {
    await updateDoc(doc(db, 'meetingTemplates', id), {
      isArchived: true,
      updatedAt: serverTimestamp()
    });
  };

  const deleteTemplate = async (id) => {
    await deleteDoc(doc(db, 'meetingTemplates', id));
  };

  // ============ Series Operations ============

  const addSeries = async (data) => {
    if (!user?.uid) throw new Error('Must be logged in');

    // Get linked users who should see this series
    const viewableBy = getLinkedUserIdsForSharing(accountLinks);

    return await addDoc(collection(db, 'meetingSeries'), {
      ...data,
      viewableBy,
      userId: user.uid,
      isArchived: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const updateSeries = async (id, data) => {
    await updateDoc(doc(db, 'meetingSeries', id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  };

  const archiveSeries = async (id) => {
    await updateDoc(doc(db, 'meetingSeries', id), {
      isArchived: true,
      updatedAt: serverTimestamp()
    });
  };

  const deleteSeries = async (id) => {
    await deleteDoc(doc(db, 'meetingSeries', id));
  };

  // ============ Meeting Operations ============

  const addMeeting = async (data) => {
    if (!user?.uid) throw new Error('Must be logged in');

    // Ensure agenda items have IDs
    const agendaItems = (data.agendaItems || []).map(item => ({
      ...item,
      id: item.id || generateUUID(),
      isCompleted: item.isCompleted ?? false,
      notes: item.notes || ''
    }));

    // Get linked users who should see this meeting
    const viewableBy = getLinkedUserIdsForSharing(accountLinks);

    return await addDoc(collection(db, 'meetings'), {
      ...data,
      agendaItems,
      viewableBy,
      actionItems: data.actionItems || [],
      notes: data.notes || null,  // Must be null or non-empty, not ''
      userId: user.uid,
      status: data.status || 'scheduled',
      isCustomized: data.isCustomized ?? false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const updateMeeting = async (id, data) => {
    await updateDoc(doc(db, 'meetings', id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  };

  const completeMeeting = async (id) => {
    await updateDoc(doc(db, 'meetings', id), {
      status: 'completed',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const cancelMeeting = async (id) => {
    await updateDoc(doc(db, 'meetings', id), {
      status: 'cancelled',
      updatedAt: serverTimestamp()
    });
  };

  const deleteMeeting = async (id) => {
    await deleteDoc(doc(db, 'meetings', id));
  };

  // ============ Series Instance Generation ============

  // Generate a virtual instance from a series for a specific date
  const generateSeriesInstance = useCallback((seriesItem, date) => {
    // Check if a real instance already exists
    const existing = meetings.find(m =>
      m.seriesId === seriesItem.id && m.date === date
    );
    if (existing) return existing;

    // Generate virtual instance (not persisted until user interacts)
    return {
      isVirtual: true,
      seriesId: seriesItem.id,
      templateId: seriesItem.templateId,
      title: seriesItem.title,
      date: date,
      startTime: seriesItem.recurrenceTime,
      endTime: calculateEndTime(seriesItem.recurrenceTime, seriesItem.durationMinutes),
      durationMinutes: seriesItem.durationMinutes,
      agendaItems: (seriesItem.defaultAgendaItems || []).map(item => ({
        ...item,
        id: generateUUID(),
        isCompleted: false,
        notes: ''
      })),
      actionItems: [],
      notes: '',
      status: 'scheduled',
      color: seriesItem.color || '#C45D3A',
      isCustomized: false,
      // Propagate shared metadata from series to virtual instance
      ...(seriesItem.isShared && {
        isShared: true,
        owner: seriesItem.owner,
        ownerDisplayName: seriesItem.ownerDisplayName,
        ownerEmail: seriesItem.ownerEmail
      })
    };
  }, [meetings]);

  // Check if a date matches a series recurrence pattern
  const dateMatchesSeries = useCallback((date, seriesItem) => {
    if (date < seriesItem.startDate) return false;
    if (seriesItem.endDate && date > seriesItem.endDate) return false;

    const dateObj = new Date(date + 'T00:00:00');
    const dayOfWeek = dateObj.getDay();
    const dayOfMonth = dateObj.getDate();

    if (seriesItem.recurrencePattern === 'weekly') {
      return seriesItem.recurrenceDay === dayOfWeek;
    }

    if (seriesItem.recurrencePattern === 'biweekly') {
      if (seriesItem.recurrenceDay !== dayOfWeek) return false;
      // Calculate weeks since start
      const startDate = new Date(seriesItem.startDate + 'T00:00:00');
      const diffTime = dateObj - startDate;
      const diffWeeks = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000));
      return diffWeeks % 2 === 0;
    }

    if (seriesItem.recurrencePattern === 'monthly') {
      return seriesItem.recurrenceDay === dayOfMonth;
    }

    return false;
  }, []);

  // Get meetings for a specific date (including virtual series instances)
  const getMeetingsForDate = useCallback((date) => {
    if (!date) return [];

    // Real meetings for this date
    const realMeetings = meetings.filter(m => m.date === date);

    // Generate virtual instances from ALL series (own + shared)
    const seriesInstances = allSeries
      .filter(s => dateMatchesSeries(date, s))
      .map(s => generateSeriesInstance(s, date))
      .filter(instance =>
        // Exclude if a real meeting already exists for this series
        !realMeetings.some(m => m.seriesId === instance.seriesId)
      );

    return [...realMeetings, ...seriesInstances]
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }, [meetings, allSeries, dateMatchesSeries, generateSeriesInstance]);

  // Persist a virtual series instance (with duplicate prevention)
  const materializeSeriesInstance = async (virtualInstance) => {
    console.log('[materialize] Starting with:', {
      isVirtual: virtualInstance.isVirtual,
      isShared: virtualInstance.isShared,
      seriesId: virtualInstance.seriesId,
      date: virtualInstance.date
    });

    if (!virtualInstance.isVirtual) return virtualInstance;
    if (!user?.uid) throw new Error('Must be logged in');

    // Shared virtual instances should not be materialized by linked users
    // Only the owner can materialize their own meetings
    if (virtualInstance.isShared) {
      console.log('[materialize] Shared instance - returning as-is');
      return virtualInstance;
    }

    const { isVirtual, ...meetingData } = virtualInstance;

    // Use deterministic ID based on seriesId and date to prevent duplicates
    if (virtualInstance.seriesId && virtualInstance.date) {
      const deterministicId = `${virtualInstance.seriesId}_${virtualInstance.date}`;
      console.log('[materialize] deterministicId:', deterministicId);

      // First, check if meeting already exists via query (avoids permission issues with get on non-existent docs)
      console.log('[materialize] Running query with userId:', user.uid);
      const existingQuery = query(
        collection(db, 'meetings'),
        where('seriesId', '==', virtualInstance.seriesId),
        where('date', '==', virtualInstance.date),
        where('userId', '==', user.uid),
        limit(1)
      );

      const existingSnapshot = await getDocs(existingQuery);
      console.log('[materialize] Query result - empty:', existingSnapshot.empty);
      if (!existingSnapshot.empty) {
        // Meeting already exists, return it
        const existingDoc = existingSnapshot.docs[0];
        console.log('[materialize] Found existing meeting:', existingDoc.id);
        return { id: existingDoc.id, ...existingDoc.data() };
      }

      // Prepare meeting data with all required fields
      const agendaItems = (meetingData.agendaItems || []).map((item) => ({
        ...item,
        id: item.id || generateUUID(),
        isCompleted: item.isCompleted ?? false,
        notes: item.notes || ''
      }));

      // Get linked users who should see this meeting
      const viewableBy = getLinkedUserIdsForSharing(accountLinks);

      const newMeetingData = {
        ...meetingData,
        agendaItems,
        viewableBy,
        actionItems: meetingData.actionItems || [],
        notes: meetingData.notes || null,  // Must be null or non-empty, not ''
        userId: user.uid,
        status: meetingData.status || 'scheduled',
        isCustomized: meetingData.isCustomized ?? false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Create with deterministic ID using setDoc
      console.log('[materialize] Creating new meeting with setDoc');
      const docRef = doc(db, 'meetings', deterministicId);
      await setDoc(docRef, newMeetingData);
      console.log('[materialize] Meeting created successfully');
      return { id: deterministicId, ...newMeetingData };
    }

    // Fallback for meetings without seriesId (shouldn't happen for virtual instances)
    const docRef = await addMeeting(meetingData);
    return { id: docRef.id, ...meetingData };
  };

  // ============ Calendar Helpers ============

  // Get meetings for a month (for calendar display)
  const getMeetingsForMonth = useCallback((year, month) => {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    return meetings.filter(m => m.date >= startDate && m.date <= endDate);
  }, [meetings]);

  // Get meeting counts per day for calendar indicators
  const getMeetingCountsByDate = useCallback((year, month) => {
    const counts = {};
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayMeetings = getMeetingsForDate(date);
      if (dayMeetings.length > 0) {
        counts[date] = dayMeetings.length;
      }
    }

    return counts;
  }, [getMeetingsForDate]);

  // Get upcoming meetings (next 7 days)
  const upcomingMeetings = useMemo(() => {
    const today = getLocalDateString(new Date());
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    const endDate = getLocalDateString(weekFromNow);

    const upcoming = [];
    let currentDate = new Date();
    for (let i = 0; i < 7; i++) {
      const dateStr = getLocalDateString(currentDate);
      const dayMeetings = getMeetingsForDate(dateStr);
      upcoming.push(...dayMeetings);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return upcoming.filter(m => m.status !== 'cancelled');
  }, [getMeetingsForDate]);

  return {
    // Data
    meetings,
    templates,
    series,
    loading,
    error,
    upcomingMeetings,

    // Template operations
    addTemplate,
    updateTemplate,
    archiveTemplate,
    deleteTemplate,

    // Series operations
    addSeries,
    updateSeries,
    archiveSeries,
    deleteSeries,

    // Meeting operations
    addMeeting,
    updateMeeting,
    completeMeeting,
    cancelMeeting,
    deleteMeeting,

    // Query helpers
    getMeetingsForDate,
    getMeetingsForMonth,
    getMeetingCountsByDate,
    materializeSeriesInstance,

    // Utilities
    generateUUID
  };
}

export { generateUUID, calculateEndTime };
